import { WidgetStreamState } from 'widgets/store/stream/WidgetStreamState';

import timeSeriesReducer from 'utils/timeseries/timeSeriesUtils';
import {
    KeyedStreamElement,
    StreamMessageElement,
} from 'communication/types/StreamElement';
import streamMessagesReducer from 'utils/messages/StreamMessage';

import { WidgetType } from 'widgets/WidgetType';

import {
    buildStreamSlice,
    useStreamSlice,
    WidgetStreamSliceActionPayloadType,
} from 'widgets/store/stream/streamSlice';

import { PortfolioReturnsDataElement } from 'communication/types/portfolioReturns/PortfolioReturnsDataElement';
import {
    initialPortfolioReturnsStreamState,
    PortfolioReturnsDataState,
} from 'widgets/portfolioReturns/store/stream/PortfolioReturnsStreamState';
import { selectPortfolioReturnsState } from 'widgets/portfolioReturns/store/selectors';
import { mapToPortfolioReturnsDataTimeSeriesItemFromElement } from 'widgets/portfolioReturns/store/stream/PortfolioReturnsDataTimeSeriesItem';
import { StreamElementType } from 'communication/types/StreamElementType';
import { PortfolioReturnsStreamElementType } from 'communication/types/portfolioReturns/PortfolioReturnsStreamElementType';
import { mapToPortfolioReturnsDataPortfolioValuesItemFromElement } from 'widgets/portfolioReturns/store/stream/PortfolioReturnsDataPortfolioValues';
import { mapToPortfolioReturnsDataInitPortfolioValuesFromElement } from 'widgets/portfolioReturns/store/stream/PortfolioReturnsDataInitPortfolioValues';
import { mapFromElementRangeArray } from 'utils/timeseries/TimeSeriesDisableRange';
import PortfolioReturnsDataMessage from 'communication/types/portfolioReturns/PortfolioReturnsDataMessage';

const processStreamElement = (
    element: PortfolioReturnsDataElement,
    nextState: WidgetStreamState<PortfolioReturnsDataState>
) => {
    const nextDataState: PortfolioReturnsDataState = nextState.data;
    timeSeriesReducer(
        element as KeyedStreamElement<StreamElementType>,
        nextDataState,
        mapToPortfolioReturnsDataTimeSeriesItemFromElement
    );
    streamMessagesReducer(element as StreamMessageElement, nextState);

    switch (element._c) {
        // Handle special messages
        case PortfolioReturnsStreamElementType.Pncs: {
            nextDataState.portfolioValues.push(
                mapToPortfolioReturnsDataPortfolioValuesItemFromElement(element)
            );
            break;
        }
        case PortfolioReturnsStreamElementType.InitVal: {
            nextDataState.initPortfolioValues =
                mapToPortfolioReturnsDataInitPortfolioValuesFromElement(
                    element
                );
            break;
        }
        case PortfolioReturnsStreamElementType.Sessions: {
            nextDataState.disableRange = mapFromElementRangeArray(element.dr);
            break;
        }
        case StreamElementType.Clear: {
            nextDataState.portfolioValues = [];
            delete nextDataState.initPortfolioValues;
            nextDataState.disableRange = [];
            break;
        }
        default: {
            break;
        }
    }
};

export const buildPortfolioReturnsStreamSlice = () =>
    buildStreamSlice<
        PortfolioReturnsDataState,
        PortfolioReturnsDataElement,
        PortfolioReturnsDataMessage
    >(
        WidgetType.PORTFOLIO_RETURNS,
        selectPortfolioReturnsState,
        initialPortfolioReturnsStreamState,
        processStreamElement
    );

export const usePortfolioReturnsStreamSlice = () => {
    return useStreamSlice(
        WidgetType.PORTFOLIO_RETURNS,
        selectPortfolioReturnsState,
        initialPortfolioReturnsStreamState,
        processStreamElement
    );
};

export const usePortfolioReturnsStreamData = () =>
    usePortfolioReturnsStreamSlice().streamData;

export type PortfolioReturnsStreamPayloadType =
    WidgetStreamSliceActionPayloadType<PortfolioReturnsDataElement>;
